import * as React from 'react'

interface LazyComponentProps {
  componentGenerator: () => JSX.Element | null
  onVisibilityChange?: (visible: boolean) => void
  placeholder?: JSX.Element
  options?: IntersectionObserverInit
  stayVisible?: boolean
}

interface LazyComponentState {
  visible: boolean
}

/**
 * Use this component when you want to render a computationally expensive component.
 * Renders the component generated by `LazyComponentProps#componentGenerator` only
 * when this component is within the viewport.
 *
 * If provided, renders `LazyComponentProps#placeholder` when this component is
 * not within the viewport.
 *
 * You can optionally change the viewport with `LazyComponentProps#options`.
 * The default viewport is the browser window viewport.
 */
export default class LazyComponent extends React.PureComponent<LazyComponentProps, LazyComponentState> {
  private ref?: React.RefObject<HTMLDivElement>
  private obserber?: IntersectionObserver

  constructor(props: LazyComponentProps) {
    super(props)
    this.state = {
      visible: false,
    }
    this.ref = React.createRef()
  }

  componentDidMount() {
    const { options, onVisibilityChange, stayVisible } = this.props
    this.obserber = new IntersectionObserver(([entry]) => {
      // intersecting with the viewport?
      const visible = entry.isIntersecting
      if (!visible && stayVisible) {
        // don't change state to not visible, if it should stay visible
        return
      }
      this.setState({ visible })
      if (onVisibilityChange) {
        onVisibilityChange(entry.isIntersecting)
      }
    }, options)

    if (this.ref?.current) {
      this.obserber.observe(this.ref.current)
    }
  }

  componentWillUnmount() {
    if (this.ref?.current && this.obserber) {
      this.obserber.unobserve(this.ref.current)
    }
  }

  render() {
    const { componentGenerator, placeholder } = this.props
    const { visible } = this.state
    return <div ref={this.ref}>{visible ? componentGenerator() : placeholder}</div>
  }
}
